import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Pipe,
	PipeTransform,
	QueryList,
	ViewChildren,
} from "@angular/core";
import { Selectable } from "common/selection/state/selectable.model";
import { SelectionStoreService } from "common/selection/state/selection-store.service";
import { assertDefined, nullToUndefined } from "core/functions";
import { Observable, of, Subject } from "rxjs";
import { Group } from "common/collapsible-groups/collapsible-groups.component";
import { SelectionRepository } from "common/selection/state/selection.repository";
import { takeUntil } from "rxjs/operators";
import { getIdKeyOfTypeId, getUpdatedSelectables } from "common/selection/helper/selection-helper";
import TypedViewable = stages.process.TypedViewable;
import ViewableDependentElement = stages.process.ViewableDependentElement;

type TypedId = stages.core.TypedId;
type ProcessTreeItem = stages.process.ProcessTreeItem;
type UpdatedElements = stages.common.UpdatedElements;

interface DependentsGroup extends Group, ProcessTreeItem {
	name: string;
	dependentElements: ViewableDependentElement[];
}

@Pipe({
	name: "getAssignableDependentElements",
	pure: true,
})
export class GetAssignableDependentElementsPipe implements PipeTransform {
	transform(
		dependentElements: ViewableDependentElement[],
		ignoreDependentsAssignable: boolean,
		restrictAssociateDependentElementTypes: string[] | undefined,
	): ViewableDependentElement[] {
		return getAssignableDependentElements(
			dependentElements,
			ignoreDependentsAssignable,
			restrictAssociateDependentElementTypes,
		);
	}
}

@Component({
	selector: "stages-viewable-selectable-browser",
	templateUrl: "./viewable-selectable-browser.component.html",
	styleUrls: ["viewable-selectable-browser.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewableSelectableBrowserComponent implements OnInit, OnDestroy {
	@Input()
	folder!: ProcessTreeItem;

	@Input()
	children?: Selectable[];

	@Input()
	allElements!: Selectable[];

	@Input()
	initialSelections!: Selectable[];

	@Input()
	filteredSelectables?: TypedId[];

	@Input()
	allowWorkspaceNavigation: boolean = true;

	@Input()
	browseRootType!: string;

	@Input()
	rootLabel?: string;

	@Input()
	translateNone?: string;

	showVisibleOnly: boolean = false;

	@Input()
	extendedMode: boolean = false;

	@Input()
	allowIndexElementsForSelection: boolean = false;

	@Input()
	storeInstanceName!: string;

	@Input()
	isDialog: boolean = true;

	@Input()
	commentsAllowed: boolean = false;

	@Input()
	explicitParentMode: boolean = true;

	@Input()
	restrictAssociateDependentElementTypes: string[] | undefined;

	@Input() // all dependent type shall be tailorable even if specified as not assignable
	ignoreDependentsAssignable: boolean = false;

	@Output() readonly addSelection = new EventEmitter<ProcessTreeItem>();

	@Output() readonly removeSelection = new EventEmitter<ProcessTreeItem>();

	@Output()
	readonly navigateToChild = new EventEmitter<ProcessTreeItem>();

	@Output()
	readonly navigateToParent = new EventEmitter<ProcessTreeItem>();

	@Output()
	readonly navigateToWorkspaces = new EventEmitter<ProcessTreeItem>();

	@Output() readonly cancel = new EventEmitter<void>();
	@Output() readonly save = new EventEmitter<UpdatedElements>();

	@ViewChildren("commentInput")
	commentInputs?: QueryList<ElementRef>;

	allSelectables$!: Observable<Selectable[]>;
	allSelectables!: Selectable[];

	selectedElements$!: Observable<Selectable[]>;
	selectedElements!: Selectable[];

	addedElements$!: Observable<Selectable[]>;
	addedElements!: Selectable[];

	removedElements$!: Observable<Selectable[]>;
	removedElements!: Selectable[];

	updatedElements$!: Observable<Selectable[]>;
	updatedElements!: Selectable[];

	selectedElementsCounter$: Observable<number> = of(0);
	selectedElementsCounter: number = 0;

	addedElementsCounter$: Observable<number> = of(0);
	addedElementsCounter: number = 0;

	removedElementsCounter$!: Observable<number>;
	removedElementsCounter: number = 0;

	isLoading$!: Observable<boolean>;

	private wasChanged: boolean = false;

	private destroy$ = new Subject<boolean>();

	contextMenuItems: MenuItem[] = [];

	isLoading: boolean = false;

	triggerChangeCounter = 0;

	constructor(private selectionStoreService: SelectionStoreService, private selectionRepository: SelectionRepository) {}

	ngOnInit(): void {
		assertDefined(this.storeInstanceName, "storeInstanceName has to be defined!");
		assertDefined(this.folder, "folder has to be defined!");
		assertDefined(this.initialSelections, "initialSelections has to be defined!");
		assertDefined(this.browseRootType, "browseRootType has to be defined!");

		if (this.extendedMode) {
			assertDefined(this.allElements, "allElements has to be defined!");
			this.createAndInitializeDynamicStore();
			this.initializeExtendedModeVariables();
		} else {
			this.selectionStoreService.createAndInitializeStoreCommon(this.initialSelections);
			this.initializeSimpleModeVariables();
		}

		this.addSubscriptions();

		this.createContextMenuItems();
	}

	private initializeSimpleModeVariables(): void {
		this.selectedElements$ = this.selectionRepository.getAllSelectedCommon$(this.storeInstanceName);
		this.selectedElementsCounter$ = this.selectionRepository.getSelectedCountByIdentifierCommon$(
			this.storeInstanceName,
		);

		this.addedElements$ = this.selectionRepository.getAllAddedCommon$(this.storeInstanceName);
		this.addedElementsCounter$ = this.selectionRepository.getAllAddedCountCommon$(this.storeInstanceName);
		this.removedElements$ = this.selectionRepository.getAllRemovedCommon$(this.storeInstanceName);

		this.removedElementsCounter$ = this.selectionRepository.getAllRemovedCountCommon$(this.storeInstanceName);
		this.updatedElements$ = this.selectionRepository.getAllUpdatedCommon$(this.storeInstanceName);
		this.isLoading$ = this.selectionRepository.isLoadingCommon$;
	}

	private createAndInitializeDynamicStore(): void {
		if (!this.extendedMode) {
			throw new Error("should only be called in advanced mode");
		}
		if (this.selectionStoreService.hasStateDynamic(this.storeInstanceName)) {
			throw new Error("dynamic store should not exist and not be cached!");
		}
		this.selectionStoreService.createAndInitializeStoreDynamic(
			this.storeInstanceName,
			this.allElements,
			this.initialSelections,
		);
	}

	private initializeExtendedModeVariables(): void {
		this.allSelectables$ = this.selectionRepository.getAllFromDynamic$(this.storeInstanceName)!;

		this.selectedElements$ = this.selectionRepository.getAllSelectedFromDynamic$(this.storeInstanceName)!;

		this.selectedElementsCounter$ = this.selectionRepository.getAllSelectedCountDynamic$(this.storeInstanceName)!;

		this.addedElementsCounter$ = this.selectionRepository.getAllAddedCountDynamic$(this.storeInstanceName)!;
		this.addedElements$ = this.selectionRepository.getAllAddedDynamic$(this.storeInstanceName)!;

		this.removedElementsCounter$ = this.selectionRepository.getAllRemovedCountDynamic$(this.storeInstanceName)!;

		this.removedElements$ = this.selectionRepository.getAllRemovedDynamic$(this.storeInstanceName)!;

		this.updatedElements$ = this.selectionRepository.getAllUpdatedDynamic$(this.storeInstanceName)!;

		this.isLoading$ = this.selectionRepository.getLoadingDynamic(this.storeInstanceName)!;

		this.allSelectables$.pipe(takeUntil(this.destroy$)).subscribe((elements) => {
			this.allSelectables = elements;
		});
	}

	private addSubscriptions(): void {
		this.selectedElements$.pipe(takeUntil(this.destroy$)).subscribe((elements) => (this.selectedElements = elements));

		this.addedElements$.pipe(takeUntil(this.destroy$)).subscribe((elements) => (this.addedElements = elements));

		this.addedElementsCounter$
			.pipe(takeUntil(this.destroy$))
			.subscribe((addedCounter) => (this.addedElementsCounter = addedCounter));

		this.selectedElementsCounter$
			.pipe(takeUntil(this.destroy$))
			.subscribe((selectedElementsCounter) => (this.selectedElementsCounter = selectedElementsCounter));

		this.removedElements$.pipe(takeUntil(this.destroy$)).subscribe((elements) => (this.removedElements = elements));

		this.removedElementsCounter$
			.pipe(takeUntil(this.destroy$))
			.subscribe((removedCounter) => (this.removedElementsCounter = removedCounter));

		this.updatedElements$.pipe(takeUntil(this.destroy$)).subscribe((elements) => (this.updatedElements = elements));

		this.isLoading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => (this.isLoading = loading));
	}

	private createContextMenuItems(): void {
		this.contextMenuItems = [
			{
				name: "common.browser.selectable.elements.addAll",
				iconClass: "ico ico-elements-add-all",
				disabled: () => this.isLoading,
				on: (element: ProcessTreeItem) => {
					this.selectionStoreService.selectAllDynamic(
						this.storeInstanceName,
						{ id: element.id, typeIdent: element.type.ident },
						false,
						this.explicitParentMode,
					);
					this.triggerChangeCounter++;
				},
			},
			{
				name: "common.browser.selectable.elements.addLeaves",
				iconClass: "ico ico-elements-add-all-in-folders",
				disabled: () => this.isLoading,
				on: (element: ProcessTreeItem) => {
					this.selectionStoreService.selectAllDynamic(
						this.storeInstanceName,
						{ id: element.id, typeIdent: element.type.ident },
						true,
						this.explicitParentMode,
					);
					this.triggerChangeCounter++;
				},
			},
			{
				name: "common.browser.selectable.elements.removeAll",
				iconClass: "ico ico-elements-remove-all",
				disabled: () => this.isLoading,
				on: (element: ProcessTreeItem) => {
					this.selectionStoreService.deselectAllDynamic(
						this.storeInstanceName,
						{
							id: element.id,
							typeIdent: element.type.ident,
						},
						this.explicitParentMode,
					);
					this.triggerChangeCounter++;
				},
			},
		];
	}

	goToWorkspaces(item: ProcessTreeItem): void {
		this.navigateToWorkspaces.emit(item);
	}

	goToChild(item: ProcessTreeItem, $event: Event): void {
		$event.stopPropagation(); // prevent toggle of expand/collapse
		this.navigateToChild.emit(item);
	}

	goToParent(item: ProcessTreeItem): void {
		this.navigateToParent.emit(item);
	}

	ngOnDestroy(): void {
		if (this.extendedMode) {
			this.selectionStoreService.deleteDynamic(this.storeInstanceName);
		}
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	getSelectionKey(item: ProcessTreeItem): string {
		return this.getSelectionKeyOfTypeId({ id: item.id, typeIdent: item.type.ident });
	}

	private getSelectionKeyOfTypeId(typedId: TypedId): string {
		return "ViewableSelectableBrowserComponent_" + this.storeInstanceName + typedId.typeIdent + "_" + typedId.id;
	}

	onSelectionChanged(itemTypeId: TypedId): void {
		const selectionKey: string = getIdKeyOfTypeId(itemTypeId);
		if (this.extendedMode) {
			const selectionTarget = this.selectionRepository.getEntityDynamic(this.storeInstanceName, selectionKey);
			this.toggleExistingDynamicStore(assertDefined(selectionTarget));
		} else {
			const selectionTarget = this.selectionRepository.getEntityCommon(selectionKey, this.storeInstanceName);
			if (selectionTarget) {
				this.toggleExistingCommon(selectionTarget);
			} else {
				this.selectionStoreService.addNewSelectableCommon(
					itemTypeId,
					true,
					false,
					this.explicitParentMode,
					true,
					undefined,
					this.storeInstanceName,
					undefined,
					undefined,
					undefined,
					undefined,
				);
			}
		}
		this.triggerChangeCounter++;
	}

	private toggleExistingCommon(selectable: Selectable): void {
		if (selectable?.isSelected) {
			this.selectionStoreService.deselectCommon(selectable, this.explicitParentMode);
		} else if (selectable && !selectable.isSelected && selectable.isSelectable) {
			this.selectionStoreService.selectCommon(selectable, this.explicitParentMode);
		}
	}

	private toggleExistingDynamicStore(selectable: Selectable): void {
		if (selectable.isSelected) {
			this.selectionStoreService.deselectDynamic(selectable, this.explicitParentMode);
		} else {
			this.selectionStoreService.selectDynamic(selectable, this.explicitParentMode);
		}
	}

	onToggleVisibleOnly(): void {
		this.showVisibleOnly = !this.showVisibleOnly;
	}

	getComment(item: ProcessTreeItem): string | null {
		const selectionKey: string = getIdKeyOfTypeId({ id: item.id, typeIdent: item.type.ident });
		const selectable = this.selectionRepository.getEntityCommon(selectionKey, this.storeInstanceName);
		return selectable ? selectable.comment : null;
	}

	updateComment(itemTypeId: TypedId): void {
		this.wasChanged = true;
		const selectionKey: string = getIdKeyOfTypeId(itemTypeId);
		const selectable = this.selectionRepository.getEntityCommon(selectionKey, this.storeInstanceName);
		if (selectable) {
			const keyToSearchFor: string = this.getSelectionKeyOfTypeId(itemTypeId);
			const allComments = this.commentInputs?.toArray();
			const newComment = allComments?.find((el) => el.nativeElement.id === keyToSearchFor);
			this.selectionStoreService.updateCommentCommon(selectable, newComment?.nativeElement.value);
		}
	}

	trackById(_index: number, item: TypedViewable): string {
		return getIdKeyOfTypeId({ id: item.id, typeIdent: item.type.ident });
	}

	isSelectable(element: ProcessTreeItem): boolean {
		if (this.extendedMode) {
			const selectionKey: string = getIdKeyOfTypeId({ id: element.id, typeIdent: element.type.ident });
			const selectable = this.selectionRepository.getEntityDynamic(this.storeInstanceName, selectionKey);
			return selectable ? selectable.isSelectable : false;
		}
		const viewable = element as stages.process.ViewableElement;
		const isSelectableChildrenOfFolder = this.isElementSelectable(element);
		const isSelectableThroughSpecialFiltering = this.isElementSelectableThroughSpecialFiltering(element);
		return this.allowIndexElementsForSelection && viewable.index
			? true
			: isSelectableChildrenOfFolder && isSelectableThroughSpecialFiltering;
	}

	private isElementSelectable(element: ProcessTreeItem): boolean {
		if (this.children) {
			const foundChild = this.children.find(
				(selectableChild) => selectableChild.id === element.id && selectableChild.type === element.type.ident,
			);
			if (foundChild) {
				return foundChild.isSelectable;
			}
		}
		return true;
	}

	private isElementSelectableThroughSpecialFiltering(element: ProcessTreeItem): boolean {
		if (this.filteredSelectables) {
			return this.filteredSelectables.some(
				(selectableTypedId) =>
					selectableTypedId.id === element.id && selectableTypedId.typeIdent === element.type.ident,
			);
		}
		return true;
	}

	onCancel(): void {
		this.cancel.emit();
	}

	onSave(): void {
		if (this.extendedMode) {
			this.selectionStoreService.setLoadingDynamic(this.storeInstanceName, true);
		} else {
			this.isLoading = true;
		}
		const updatedElements: UpdatedElements = getUpdatedSelectables(
			this.addedElements,
			this.removedElements,
			this.updatedElements,
		);
		this.save.emit(updatedElements);
	}

	get isBrowseRoot(): boolean {
		if (this.folder.type.ident === "process") {
			return true;
		}

		if (nullToUndefined(this.folder.parent) === undefined) {
			return true;
		}
		const parent = assertDefined(this.folder.parent);
		return this.folder.type.ident !== parent.type.ident && this.browseRootType !== "process";
	}

	get isDirty(): boolean {
		return this.wasChanged || this.addedElementsCounter > 0 || this.removedElementsCounter > 0;
	}

	getGroupInfo(item: ProcessTreeItem): DependentsGroup {
		return {
			...item,
			name: item.label,
			ident: item.type.ident + item.id,
			showEmpty: true,
			getMemberCount(): number {
				if (item.dependentElements) {
					return item.dependentElements.length;
				}
				return 0;
			},
			dependentElements: item.dependentElements ?? [],
		};
	}

	showDependentElements(item: ProcessTreeItem): boolean {
		return (
			this.isSelectable(item) &&
			item.dependentElements !== undefined &&
			getAssignableDependentElements(
				item.dependentElements,
				this.ignoreDependentsAssignable,
				this.restrictAssociateDependentElementTypes,
			).length > 0
		);
	}
}

function getAssignableDependentElements(
	dependentElements: ViewableDependentElement[],
	ignoreDependentsAssignable: boolean,
	restrictAssociateDependentElementTypes: string[] | undefined,
): ViewableDependentElement[] {
	const assignableDependentElements = dependentElements.filter(
		(dependentElement) => ignoreDependentsAssignable || dependentElement.type.isAssignableDependentElement,
	);
	if (!restrictAssociateDependentElementTypes) {
		return assignableDependentElements;
	}
	return assignableDependentElements.filter(
		(dependentElement) =>
			dependentElement.type.subtypeIdent &&
			restrictAssociateDependentElementTypes.includes(dependentElement.type.subtypeIdent),
	);
}
